import React, { FC, ReactNode, useState } from "react"
import {
    Button,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    DialogProps,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { useNotify } from "react-admin"
import { useTranslate } from "ra-core"

interface BaseModalProps extends DialogProps{
    title: string
    children: ReactNode
    open: boolean
    sendNotify?: boolean
    onClose: () => void
    onConfirm?: () => Promise<void> | void
}

const BaseModal: FC<BaseModalProps> = ({ 
    title, 
    children, 
    open,
    sendNotify = false,
    onClose, 
    onConfirm,
    ...rest
}) => {
    const [loading, setLoading] = useState(false)
    const notify = useNotify()
    const translate = useTranslate()

    const handleConfirm = () => {
        if (onConfirm) {
            setLoading(true)
            
            const result = onConfirm()
            
            if (result && typeof result.then === 'function') {
                result.then(() => {
                    if (sendNotify) {
                        notify(translate('internal.success'), { type: 'success' })
                    }
                    setLoading(false)
                    onClose()
                }).catch((error) => {
                    if (sendNotify) {
                        notify(translate('internal.error'), { type: 'error' })
                    }
                    setLoading(false)
                })
            } else {
                if (sendNotify) {
                    notify(translate('internal.success'), { type: 'success' })
                }
                setLoading(false)
                onClose()
            }
        } else {
            onClose()
        }
    }

    return (
        <Dialog open={open} onClose={onClose} {...rest}>
            <DialogTitle>
                {title}
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={onClose}
                    aria-label="close"
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: 8,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {children}
            </DialogContent>
            {onConfirm &&
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    {translate("internal.cancel")}
                </Button>
                <Button onClick={handleConfirm} color="primary" disabled={loading}>
                    {translate("internal.confirm")}
                </Button>
            </DialogActions>}
        </Dialog>
    )
}


export const useBaseModal = () => {
    const [open, setOpen] = useState(false)
    
    const handleOpen = () => setOpen(true)
    const handleClose = () => setOpen(false)
    
    return { open, handleOpen, handleClose }
}

export default BaseModal